
import { Grid } from '@pokemon/design-v2.grid'
import { FC, useContext, useMemo } from 'react'
import { composeValidators, maxLength, required } from '../verify/validation'
import { useTranslation } from 'react-i18next'
import { AuthContext, AuthContextValue } from '../../utils/auth/context'
import { definedStateCountries, PTCSStateField } from './state_field'
import { Field, FieldInputProps, FieldMetaState, FieldProps } from 'react-final-form'
import { TextField } from '@pokemon/design-v2.text-field'

const AddressFieldWrapper = ({ halfWidth, fieldProps, renderInput } : {halfWidth?: boolean, fieldProps: FieldProps<any, any, any>, renderInput: FC<{input: FieldInputProps<any, HTMLElement>, meta: FieldMetaState<any>}>}) => {
  return <Grid
        xs={halfWidth ? 6 : 12}
    >
        <Field
            {...fieldProps}
        >
            {({ input, meta }) => {
              return renderInput({ meta, input })
            }}
        </Field>
    </Grid>
}

export const PTCSAddressForm = () => {
  const { loggedUserProfile } = useContext<AuthContextValue>(AuthContext)
  const { t } = useTranslation()

  const validators = useMemo(
    () => {
      const validators = {
        address_1: [
          required(t('verification.ptcs.form.errors.address_1.required', 'Address is required')),
          maxLength(
            t('verification.ptcs.form.errors.address_1.max_length', 'Address cannot be longer than 100 characters'),
            100
          )
        ],
        address_2: [
          maxLength(
            t('verification.ptcs.form.errors.address_2.max_length', 'Apt/Suite cannot be longer than 100 characters'),
            100
          )
        ],
        city: [
          required(t('verification.ptcs.form.errors.city.required', 'City is required')),
          maxLength(
            t('verification.ptcs.form.errors.city.max_length', 'City cannot be longer than 100 characters'),
            100
          )
        ],
        zip: [
          required(t('verification.ptcs.form.errors.zip.required', 'Zip code is required')),
          maxLength(t('verification.ptcs.form.errors.zip.max_length', 'Postal code cannot be longer than 10 characters'), 10)
        ],
        state: [
          maxLength(
            t('verification.ptcs.form.errors.state.max_length', 'State cannot be longer than 100 characters'),
            100
          )
        ]
      }
      if ((definedStateCountries.includes(loggedUserProfile?.country_code!))) {
        validators.state.push(
          required(t('verification.ptcs.form.errors.state.required', 'State/Province is required'))
        )
      }
      return validators
    },
    [t, loggedUserProfile?.country_code]
  )

  return <Grid
        container
        spacing={2}
        sx={{
          '& .MuiTextField-root': {
            width: '100%'
          }
        }}
        key="addressgrid"
    >
        <AddressFieldWrapper
            fieldProps={{
              name: 'address1',
              validate: composeValidators(...validators.address_1)
            }}
            renderInput={({ input, meta }) => {
              return <TextField
                    {...input}
                    id="address1"
                    errorText={meta.touched && meta.error}
                    label={t('address', 'Address')}
                />
            }}
        />
        <AddressFieldWrapper
            fieldProps={{
              name: 'address2',
              validate: composeValidators(...validators.address_2)
            }}
            renderInput={({ input, meta }) => {
              return <TextField
                    {...input}
                    id="address2"
                    errorText={meta.touched && meta.error}
                    label={t('address_line_2', 'Address Line 2')}
                />
            }}
        />
        <AddressFieldWrapper
            fieldProps={{
              name: 'city',
              validate: composeValidators(...validators.city)
            }}
            halfWidth
            renderInput={({ input, meta }) => {
              return <TextField
                    {...input}
                    id="city"
                    errorText={meta.touched && meta.error}
                    label={t('city', 'City')}
                />
            }}
        />
        <AddressFieldWrapper
            fieldProps={{
              name: 'state',
              validate: composeValidators(...validators.state)
            }}
            halfWidth
            renderInput={({ input, meta }) => {
              return <PTCSStateField
                    {...input}
                    id="state"
                    halfWidth
                    label={t('address_state_province', 'State/Province')}
                    countryCode={loggedUserProfile?.country_code!}
                    errorText={meta.touched && meta.error}
                />
            }}
        />
        <AddressFieldWrapper
            fieldProps={{
              name: 'postal_code',
              validate: composeValidators(...validators.zip)
            }}
            renderInput={({ input, meta }) => {
              return <TextField
                    {...input}
                    id="zip"
                    errorText={meta.touched && meta.error}
                    label={t('address_zip', 'ZIP/Postal Code')}
                />
            }}
        />
    </Grid>
}
